import type { PublicVenue } from '@/types/events'

// Only the address fields are read; the time zone is irrelevant here.
type VenueAddress = Omit<PublicVenue, 'timezone'>

/**
 * Build the single-line display label for a venue.
 *
 * Every field inside a venue payload is individually nullable, so the label is
 * built defensively: the name when there is one, otherwise the street and city
 * joined, otherwise null. A null return means "render nothing" - no empty
 * element, no dangling separator, no pin icon.
 */
export function venueLabel(venue: VenueAddress | null | undefined): string | null {
  if (venue === null || venue === undefined) return null

  if (venue.name !== null && venue.name !== undefined && venue.name.trim() !== '') {
    return venue.name
  }

  const parts = [venue.street, venue.city].filter(
    (part): part is string => part !== null && part.trim() !== '',
  )

  return parts.length > 0 ? parts.join(', ') : null
}

/**
 * Build the one-line postal address for a venue, excluding its name.
 *
 * Used where the name is already rendered on its own line and the address is
 * shown beneath it. Returns null when no address part is available.
 */
export function venueAddressLine(venue: VenueAddress | null | undefined): string | null {
  if (venue === null || venue === undefined) return null

  const cityLine = [venue.postal_code, venue.city]
    .filter((part): part is string => part !== null && part.trim() !== '')
    .join(' ')

  const parts = [venue.street, cityLine === '' ? null : cityLine, venue.country].filter(
    (part): part is string => part !== null && part.trim() !== '',
  )

  return parts.length > 0 ? parts.join(', ') : null
}

/**
 * Build the one-line public venue label: "Venue name, City".
 *
 * Used on the public registration pages (race list, wizard summary,
 * confirmation). Deliberately excludes street, postal code and country.
 *
 * Degradation: with a non-blank name, "name, city" or the name alone. Without
 * a non-blank name, falls back to venueLabel() ("Street, City", the city alone,
 * or null) so a name-less venue never shows less than it did before. A null
 * return means "render nothing".
 */
export function venueNameCityLine(venue: VenueAddress | null | undefined): string | null {
  if (venue === null || venue === undefined) return null

  if (venue.name === null || venue.name === undefined || venue.name.trim() === '') {
    return venueLabel(venue)
  }

  const parts = [venue.name, venue.city].filter(
    (part): part is string => part !== null && part !== undefined && part.trim() !== '',
  )

  return parts.join(', ')
}
